import {
	AutomaticBreakDeductionPolicy,
	AutomaticOvertimeCappingPeriod,
	BreakModelSelectionStatus,
	BreakTimeRecordingMode,
	BudgetPlanningType,
	CompensationType,
	CustomFieldsForRecordType,
	CutHandlingMode,
	DateTimeChangeableType,
	DeletedStatus,
	DurationFormat,
	DurationUnit,
	FeatureModuleKey,
	FeatureModuleStatus,
	FixedBreakType,
	FlexTimeIndicator,
	FlexTimeIndicatorType,
	HolidayCalendarGenerationMode,
	HolidayDayDuration,
	IntegrationType,
	InvitationStatus,
	MailDeliveryStatus,
	MobileTaskSelectionType,
	ModuleDiscountType,
	NormalWorkingTimeEvaluationMode,
	OvertimeDeductionAccountType,
	Platform,
	PresenceBoardCategory,
	PresenceBoardVisibility,
	ProjectTimeValidationCategory,
	ProofRequirement,
	ProofReviewAction,
	ProofStatus,
	RecordedEntryStatus,
	RecordedEntryStatusKey,
	RecordingLockType,
	RecordingMode,
	RecordingValidationStatus,
	RevisionType,
	RunningTimeStatus,
	TargetWorkingTimeType,
	TimeAccountEntryType,
	TimeAccountState,
	TravellingTimeTargetAccount,
	VacationUnit,
	ValidationSeverity,
	WeeklyRestPeriodStrategyType,
	WorkingTimeNoticeMode,
	WorkingTimeRequestStatus,
	WorkingTimeRequestStepConfigurationType,
	WorkingTimeRequestStepStatus,
	WorkingTimeRequestStepType,
	WorkingTimeTypeCategory,
	WorkingTimeTypeRecordingMode,
	WorkingTimeTypeSubCategory,
	WorkingTimeValidationCategory,
	WorkScheduleModelType
} from "@/shared/timrTypes";
import type {TimrFeature} from "@/shared/timrFeature";

declare global {
	let messages: {
		[message: string]: string;
	} & {
		withArguments: (message: string, ...args: (string | number | boolean)[]) => string;
	};
	let gtag: (...args: unknown[]) => void;

	interface Window {
		USETIFUL: unknown;
	}
}

export interface TIMRGlobals {
	currency: string;
	customTaskField1: string;
	customTaskField2: string;
	customTaskField3: string;
	dateFormat: string;
	dateFormatWithoutYear: string;
	dateFormatForParsing: string;
	workingTimeDateTimeChangeable: boolean;
	projectTimeDateTimeChangeable: boolean;
	driveLogDateTimeChangeable: boolean;
	workingTimeDateTimeChangeableType: DateTimeChangeableType;
	projectTimeDateTimeChangeableType: DateTimeChangeableType;
	driveLogDateTimeChangeableType: DateTimeChangeableType;
	dateTimeFormat: string;
	decimalSeparator: string;
	driveLogEnabled: boolean;
	durationFormat: DurationFormat;
	firstDayOfWeek: number;
	isAdmin: boolean;
	isAuthorizedForAllUsers: boolean;
	isTaskManager: boolean;
	isGroupLeader: boolean;
	isTaskLeader: boolean;
	ownerId: number;
	isOwner: boolean;
	isWorkingTimeRequestsOverrideAllowed: boolean;
	language: string;
	manualBreaksAllowed: boolean;
	breakTimeRecordingMode: BreakTimeRecordingMode;
	isProjectTimeRecordingSectionVisible: boolean;
	projectTimeEnabled: boolean;
	showTimeZone: boolean;
	timeFormat: string;
	timeFormatForParsing: string;
	timeZonePattern: RegExp;
	userId: number;
	userFullname: string;
	userLogin: string;
	workingTimeEnabled: boolean;
	workingTimeRequestsEnabled: boolean;
	validateWorkingTime: boolean;
	validateWorkingTimeOverlaps: boolean;
	validateProjectTimeOverlaps: boolean;
	validateProjectTimeExistsWorkingTimes: boolean;
	monthNames: string[];
	monthNamesShort: string[];
	dayNames: string[];
	dayNamesShort: string[];
	showBudgetInformationForUsers: boolean;
	savedValidationsEnabled: boolean;
	roundingModeWorkingTime: string;
	roundingModeProjectTime: string;
	roundingModeDriveLog: string;
	roundToMinutesWorkingTime: number;
	roundToMinutesProjectTime: number;
	roundToMinutesDriveLog: number;
	recordingMode: RecordingMode;
	allowedToChangeBillable: boolean;
}

/**
 * Do not add const enums here as they will be inlined during compilation and thus won't be available at runtime.
 * Add enums instead in timrTypes.ts
 */

export interface Identifiable {
	id: number;
}

export interface SecurityStatusDTO {
	addTaskAllowed: boolean;
	addDriveLogCategoryAllowed: boolean;
	addUserAllowed: boolean;
	addWorkingTimeTypeAllowed: boolean;
	addCarAllowed: boolean;
	startWorkingTimeAllowed: boolean;
	addWorkingTimeAllowed: boolean;
	startProjectTimeAllowed: boolean;
	addProjectTimeAllowed: boolean;
	startDriveLogAllowed: boolean;
	addDriveLogAllowed: boolean;
	revisionsAllowed: boolean;
	corporateFeaturesAllowed: boolean;
	budgetsAllowed: boolean;
	timeAccountAllowed: boolean;
	lockTaskAllowed: boolean;
	recordingRestrictionsAllowed: boolean;
	workingTimeRequestsAllowed: boolean;
	taskLocationsAllowed: boolean;
	geofenceAllowed: boolean;
	passwordChangeAllowed: boolean;
	overtimeAllowanceAllowed: boolean;
	travellingTimeAllowed: boolean;
	trustBasedWorkingTimeAllowed: boolean;
	enhancedWorkingTimeTypesAllowed: boolean;
	distanceAutocompletionAllowed: boolean;
	customFieldsForRecordAllowed: boolean;
	tourSyncAllowed: boolean;
	externalUserSyncAllowed: boolean;
	disableBrandingAllowed: boolean;
	presenceBoardAllowed: boolean;
	vacationAccountAllowed: boolean;
	holidayCalendarAllowed: boolean;
	substitutesAllowed: boolean;
	paidTimeOffCompensationAllowed: boolean;
	projectTimeAttachmentsAllowed: boolean;
	workingTimeRequestsMultistepAllowed: boolean;
	workingTimeRequestsIndividualAllowed: boolean;
	alternatingWorkScheduleModelsAllowed: boolean;
	proofsAllowed: boolean;
	workingTimeNoticesAllowed: boolean;
}

/** Mirrors Java {@code com.troii.timr.model.PartialUser}. */
export interface PartialUser extends Identifiable {
	id: number;
	firstname: string;
	lastname: string;
	fullname: string;
	email?: string;
	employeeNumber?: string;
	externalId?: string;
	userId?: string;
	login?: string;
}

export interface User extends Identifiable {
	elementType: "USER";
	version: number;
	fullname: string;
	lastname: string;
	firstname: string;
	disabled: boolean;
	login: string;
	admin: boolean;
	taskManager: boolean;
	taskLeader: boolean;
	overallTaskLeader: boolean;
	subscriptionManager?: boolean;
	userSettings: {
		workingTimeRequestStatusEmail: boolean;
		unsubscribed?: string | boolean;
		runningTimerWarningAfterHour?: number;
	};
	invitation?: {
		status: InvitationStatus;
	};
	vacationUnit?: VacationUnit;
	entryDate?: string;
	resignDate?: string;
	email?: string;
	externalId?: string;
	employeeNumber?: string;
	hourlyRate?: number;
	hourlyRateInternal?: number;
	overtimeDurationIncluded?: number;
	overtimeAllowanceAllIn?: boolean;
	allowClearNegativeWorkDurationBalance?: boolean;
	allInSetWorkDurationBalanceZero?: boolean;
	overtimeDeductionAccountOrder?: OvertimeDeductionAccountType[];
	vacationYearStart?: string;
	vacationDaysPerYear?: number;
	workHoursPerWeek?: number;
	holidayCalendarId?: number;
	workScheduleModelId?: number;
	workScheduleModelName?: string;
	startOfFirstAlternatingWeek: string | null;
	breakModelId?: number;
	workingTimeDateTimeChangeableOverride?: boolean;
	projectTimeDateTimeChangeableOverride?: boolean;
	driveLogDateTimeChangeableOverride?: boolean;
	workingTimeRecordingMobileAllowedOverride?: boolean;
	projectTimeRecordingMobileAllowedOverride?: boolean;
	driveLogRecordingMobileAllowedOverride?: boolean;
	mailDeliveryStatus?: MailDeliveryStatus;
	trustBasedWorkingTime: boolean;
	workingTimeTracking: boolean;
	vacationAccount: boolean;
	lastTimeAccountBalance?: string;
	nextTimeAccountBalanceFromDate?: string;
	timeAccountState?: TimeAccountState;
	externalTerminalAuthenticationKeySet?: boolean;
}

/** Mirrors Java {@code AssignedUserDTO} ({@code extends UserDTO}). */
export interface AssignedUser extends User {
	autoAssigned: boolean;
	hoursPlanned?: number;
	hoursPlannedManually?: boolean;
	recordingLock?: RecordingLockType;
}

/** Mirrors Java {@code com.troii.timr.model.Invitation}. */
export interface Invitation {
	id: number;
	created: string;
	status: InvitationStatus;
}

export interface WorkingTimeDateSpan extends Identifiable {
	version: number;
	user: User;
	workingTimeType: WorkingTimeType;
	description: string;
	start: string;
	end: string;
	startHalfDay: boolean;
	endHalfDay: boolean;
	statusComment: string;
	substitute: User | null;
	requiresSubstitute: boolean;
	created: string;
	lastModified: string;
	modifiedByUser: User | null;
}

export interface FindWorkingTimeDateSpanResponse {
	workingTimeDateSpan: WorkingTimeDateSpan;
	validations: ValidationResult[];
}

export interface WorkingTimeDateSpanCalendarEntry extends WorkingTimeDateSpan {
	editable: boolean;
}

export interface PartialWorkingTimeType extends Identifiable {
	id: number;
	name: string;
	shortName?: string;
	description?: string;
	externalId?: string;
	workingTimeTypeId?: string;
	category: WorkingTimeTypeCategory;
	subCategory: WorkingTimeTypeSubCategory | null;
	editUnit: DurationUnit;
	requiresSubstitute: boolean;
}

export interface WorkingTimeType extends Identifiable {
	name: string;
	shortName: string;
	description: string;
	version: number;
	externalId: string | null;
	workingTimeTypeId: string;
	category: WorkingTimeTypeCategory;
	subCategory: WorkingTimeTypeSubCategory | null;
	editUnit: DurationUnit;
	archived: boolean;
	recordingModeUser: WorkingTimeTypeRecordingMode;
	nonCreditableDeductible: number;
	creditability: number;
	compensationDeductible: number;
	compensationCreditability: number;
	compensationType: CompensationType;
	normalWorkingTimeEvaluationMode: NormalWorkingTimeEvaluationMode;
	initiated: boolean;
	requiresSubstitute: boolean;
	travellingTimeTargetAccountWithinNormalWorkingTime?: TravellingTimeTargetAccount;
	travellingTimeTargetAccountOutsideNormalWorkingTime?: TravellingTimeTargetAccount;
	automaticBreakDeductionPolicy: AutomaticBreakDeductionPolicy;
	workingTimeRequestStepsConfiguration: WorkingTimeRequestStepConfiguration[] | null;
	workingTimeRequestStepsEmailNotificationEnabled: boolean;
	proofRequirement: ProofRequirement;
	proofRequiredAfterDays: number | null;
	location?: Location;
	locationRestrictionRadius?: number;
	noticeMode: WorkingTimeNoticeMode;
}

export interface Location {
	latitude?: number;
	longitude?: number;
	address?: string;
	city?: string;
	zipCode?: string;
	state?: string;
	countryId?: number;
	geohash?: string;
}

/**
 * A working time notice, both as a calendar entry and as one audit revision in the dialog's history table.
 * `lastModified` is an ISO instant (UTC) and is rendered in the viewer's timezone.
 */
export interface WorkingTimeNotice extends Identifiable {
	version: number;
	userId: number;
	userFullname: string;
	workingTimeTypeId: number;
	workingTimeTypeName: string;
	workingTimeTypeCategory: WorkingTimeTypeCategory;
	noticeMode: WorkingTimeNoticeMode;
	description?: string;
	start: string;
	end: string;
	lastModified: string;
	modifiedByUser?: string;
	/** Whether the notice's period is already balanced, making it read-only (see isNoticeReadOnly). */
	balanced: boolean;
	/** The request this notice was created from; only delivered by the timeline. */
	requestId?: number;
}

export interface SaveWorkingTimeNoticeRequest {
	id?: number;
	version?: number;
	userId?: number;
	workingTimeTypeId?: number;
	description?: string;
	start: string;
	end: string;
}

export interface TaskLocationData {
	geohash: string;
	latitude: number;
	longitude: number;
	address: string;
	city: string;
	zipCode: string;
	state: string;
	countryId: number;
	countryName: string;
	locationRestrictionRadius: number;
	locationInherited: boolean;
	locationRestrictionInherited: boolean;
}

export interface Task extends Identifiable, TaskLocationData {
	name: string;
	version?: number;
	description: string;
	breadCrumbs: string;
	billable: boolean;
	bookable?: boolean;
	numberOfChildren?: number;
	assignedToCurrentUser?: boolean;
	subtasks?: Task[];
	disabled?: boolean;
	descriptionRequired?: boolean;
	aiValidationPrompt?: string;
	parentTask?: Task;
	hourlyRate?: number;
	hoursPlanned?: number;
	budgetPlanned?: number;
	budgetPlanningType?: BudgetPlanningType;
	budgetPlanningTypeInherited?: boolean;
	budgetSpecified?: boolean;
	parentBudgetPlanningType?: BudgetPlanningType;
	parentHourlyRate?: number;
	parentBudgetCountNotBillableHours?: boolean;
	currentUserIsLeader?: boolean;
	externalComments?: string;
	start?: string;
	end?: string;
	lockDate?: string;
	lockDateInherited?: string;
	earliestStartTime?: string;
	latestEndTime?: string;
	externalId?: string;
	customField1?: string;
	customField2?: string;
	customField3?: string;
	budgetCountNotBillableHours?: boolean;
	taskId?: string;
}

export interface Car extends Identifiable {
	plate: string;
	name: string;
	usable?: boolean;
	endOfLastDrive?: number;
	externalId?: string;
	carId?: string;
	version?: number;
}

/**
 * Mirrors Java {@code AbstractBaseGroupDTO}. Shared base for {@link Group}
 * and {@link GroupWithChildren}.
 */
export interface AbstractBaseGroup extends Identifiable {
	elementType: "GROUP";
	name: string;
	description?: string;
	version: number;
	numberOfChildren: number;
	breadCrumbs?: string;
	currentUserIsLeader?: boolean;
	externalId?: string;
	groupId?: string;
	/** `TreeElement.getKey()` — typed `string` to match `Fancytree.getNodeByKey`. */
	key: string;
	folder: boolean;
	lazy: boolean;
	requiresSubstituteOverride: boolean | null;
}

/** Mirrors Java {@code GroupDTO}. */
export interface Group extends AbstractBaseGroup {
	parentGroup?: Group;
}

/**
 * Mirrors Java {@code GroupWithSubGroupsDTO}. Returned by the `filter*Groups.json`
 * endpoints. JSON property is `children` — the Java field `subgroups` is
 * `@JsonIgnore`'d and serialized via `getChildren()`.
 */
export interface GroupWithChildren extends AbstractBaseGroup {
	children: GroupWithChildren[];
}

export interface AssignedGroup extends Group {
	autoAssigned: boolean;
	hourlyRate?: boolean;
}

/**
 * Mirrors Java {@code TimeAccountDTO}. Response shape of
 * `/timeaccounts/correctVacationBalance.json` and `/correctWorkBalance.json`.
 * **Not** the table row shape — see {@link TimeAccountTableRow}.
 */
export interface TimeAccount extends Identifiable {
	version: number;
	user: User;
	/** ISO date string "yyyy-MM-dd". */
	date: string;
	dateFrom: string;
	dateTo: string;
	workScheduleModelName?: string;
	workDurationTarget?: number;
	workDurationActual?: number;
	paidTimeOffDuration?: number;
	unpaidTimeOffDuration?: number;
	holidayDuration?: number;
	vacationDuration?: number;
	workDurationTotal?: number;
	workDurationBalance?: number;
	deductedOvertimeDuration?: number;
	workBalanceCorrections?: number;
	workBalanceTransfers?: number;
	workBalancePayoffs?: number;
	workDurationTotalBalance?: number;
	lastWorkDurationTotalBalance?: number;
	vacationNew?: number;
	vacationConsumed?: number;
	vacationCorrections?: number;
	vacationTotalBalance?: number;
	lastVacationTotalBalance?: number;
	completeHomeOfficeDays?: number;
	partialHomeOfficeDays?: number;
	vacationUnit: VacationUnit;
	description: string;
	timeAccountEntryType: TimeAccountEntryType;
	/** ISO 8601 timestamp, serialized from Java {@code Instant} (e.g. "2026-04-10T10:30:00Z"). */
	created: string;
	createdByUser: User;
	trustBasedWorkingTime: boolean;
	workingTimeTracking: boolean;
	vacationAccount: boolean;
	vacationPayoffs?: number;
	flexTimeIndicator: FlexTimeIndicator;
}

/** Mirrors Java: <code>com.troii.timr.web.model.TimeAccountTableRow</code> **/
export interface TimeAccountTableRow extends Identifiable {
	userId: number;
	userFullname: string;
	userWorkScheduleModelId: number;
	userHolidayCalendarId: number;
	userVacationYearStart: string;
	userVacationDaysPerYear: number;
	userOvertimeDeductionAccountOrder: OvertimeDeductionAccountType[];
	userAllowClearNegativeWorkDurationBalance: boolean;
	userAllInSetWorkDurationBalanceZero: boolean;
	date: string;
	dateFrom: string;
	dateTo: string;
	description: string;
	timeAccountEntryType: TimeAccountEntryType;
	deletable: boolean;
	created: string;
	createdByUserName: string;
	trustBasedWorkingTime: boolean;
	vacationAccount: boolean;
	workingTimeTracking: boolean;
	workDurationTarget: number | null;
	workDurationActual: number | null;
	workDurationTotal: number | null;
	workDurationBalance: number | null;
	workDurationTotalBalance: number | null;
	lastWorkDurationTotalBalance: number | null;
	paidTimeOffDuration: number | null;
	unpaidTimeOffDuration: number | null;
	holidayDuration: number | null;
	vacationDuration: number | null;
	workBalanceCorrections: number | null;
	workBalanceTransfers: number | null;
	workBalancePayoffs: number | null;
	deductedOvertimeDuration: number | null;
	vacationCorrections: number | null;
	vacationPayoffs: number | null;
	overtimeDurationIncluded: number | null;
	overtimeAllowanceAllIn: boolean | null;
	workScheduleModelAlternatingStartWeek?: number | null;
	overtimes?: TimeAccountOvertime[];
	overtimeSum?: number;
	overtimeAllowanceBalanceSum?: number;
	overtimeBalance?: number;
	allowanceBalance?: number;
	allowancesOnly?: boolean;
	travellingTimes?: TimeAccountTravellingTime[];
	travellingTimeSum?: number;
	travellingTimeBalanceSum?: number;
	vacationNew: number | null;
	vacationConsumed: number | null;
	vacationTotalBalance: number | null;
	lastVacationTotalBalance: number | null;
	vacationUnit: VacationUnit | null;
}

export interface TimeAccountBaseAccount extends Identifiable {
	version: number;
	lastAllowanceDurationTotal: number;
	allowanceForDuration: number;
	allowanceDuration: number;
	allowanceCorrections: number;
	allowanceTransfers: number;
	allowancePayoffs: number;
	allowanceDurationTotal: number;
	lastOvertimeDurationTotal: number;
	overtimeDuration: number;
	overtimeCorrections: number;
	overtimeTransfers: number;
	overtimePayoffs: number;
	overtimeDurationTotal: number;
	lastTimeAccountDurationTotal: number;
	timeAccountDuration: number;
	timeAccountCorrections: number;
	timeAccountDurationTotal: number;
	totalDuration: number;
	percentage: number;
}

export interface TimeAccountOvertime extends TimeAccountBaseAccount {
	overtimeRule: OvertimeRule;
}

export interface TimeAccountTravellingTime extends TimeAccountBaseAccount {
	subCategory: WorkingTimeTypeSubCategory;
}

export interface CurrentTimeAccount {
	user: User;
	lastTimeAccountBalanceDate: string;
	lastWorkDurationTotalBalance: number;
	workDurationBalance: number;
	workDurationTotalBalance: number;
	vacationConsumed: number;
	vacationPlanned: number;
	vacationLeft: number;
	vacationUnit: VacationUnit;
	overtimes: TimeAccountOvertime[];
	overtimeSum: number;
	travellingTimes: TimeAccountTravellingTime[];
	travellingTimeSum: number;
	allowancesOnly: boolean;
	trustBasedWorkingTime: boolean;
	vacationAccount: boolean;
	workingTimeTracking: boolean;
	flexTimeIndicator: FlexTimeIndicator;
	vacationYearStart: string;
}

export interface FixedBreak {
	from1: string;
	to1: string;
	from2: string;
	to2: string;
	from3: string;
	to3: string;
}

export interface BreakModel extends Identifiable {
	name: string;
	version?: number;
	variableBreak1WorkDuration?: number;
	variableBreak1BreakDuration?: number;
	variableBreak2WorkDuration?: number;
	variableBreak2BreakDuration?: number;
	variableBreak3WorkDuration?: number;
	variableBreak3BreakDuration?: number;
	deductLegalMinimumBreaks: boolean;
	fullyDeductLegalMinimumBreaks: boolean;
	fixedBreakType: FixedBreakType;
	fixedBreak: FixedBreak;
	fixedBreakMonday: FixedBreak;
	fixedBreakTuesday: FixedBreak;
	fixedBreakWednesday: FixedBreak;
	fixedBreakThursday: FixedBreak;
	fixedBreakFriday: FixedBreak;
	fixedBreakSaturday: FixedBreak;
	fixedBreakSunday: FixedBreak;
	minimumManualBreakDuration?: number;
	applyToProjectTime?: boolean;
	paidBreakDuration?: number;
	paidBreakWorkDuration?: number;
	dailyBreakDuration?: number;
	dailyBreakWorkDuration?: number;
	dailyBreakConsiderExistingBreaks?: boolean;
	dailyBreakHardCut?: boolean;
	maximumWorkDurationDaily?: number;
	cutExceedingDaily: boolean;
	maximumWorkDurationWeekly?: number;
	flexTimeEnabled: boolean;
	flexTimeStartMonday?: string;
	flexTimeStartTuesday?: string;
	flexTimeStartWednesday?: string;
	flexTimeStartThursday?: string;
	flexTimeStartFriday?: string;
	flexTimeStartSaturday?: string;
	flexTimeStartSunday?: string;
	flexTimeEndMonday?: string;
	flexTimeEndTuesday?: string;
	flexTimeEndWednesday?: string;
	flexTimeEndThursday?: string;
	flexTimeEndFriday?: string;
	flexTimeEndSaturday?: string;
	flexTimeEndSunday?: string;
	coreTime1StartMonday?: string;
	coreTime1StartTuesday?: string;
	coreTime1StartWednesday?: string;
	coreTime1StartThursday?: string;
	coreTime1StartFriday?: string;
	coreTime1StartSaturday?: string;
	coreTime1StartSunday?: string;
	coreTime1EndMonday?: string;
	coreTime1EndTuesday?: string;
	coreTime1EndWednesday?: string;
	coreTime1EndThursday?: string;
	coreTime1EndFriday?: string;
	coreTime1EndSaturday?: string;
	coreTime1EndSunday?: string;
	coreTime2StartMonday?: string;
	coreTime2StartTuesday?: string;
	coreTime2StartWednesday?: string;
	coreTime2StartThursday?: string;
	coreTime2StartFriday?: string;
	coreTime2StartSaturday?: string;
	coreTime2StartSunday?: string;
	coreTime2EndMonday?: string;
	coreTime2EndTuesday?: string;
	coreTime2EndWednesday?: string;
	coreTime2EndThursday?: string;
	coreTime2EndFriday?: string;
	coreTime2EndSaturday?: string;
	coreTime2EndSunday?: string;
	validateDailyRestPeriod?: boolean;
	validateDailyRestPeriodDuration?: number;
	validateWeeklyRestPeriodStrategy?: WeeklyRestPeriodStrategyType;
	validateWeeklyRestPeriodStart?: string;
	validateHolidayRestPeriod?: boolean;
	validateHolidayRestPeriodStart?: string;
	cutBeforeNormalWorkingTimeStarts?: boolean;
	cutAfterNormalWorkingTimeEnds?: boolean;
	cutBeforeFlexTimeStarts?: boolean;
	cutAfterFlexTimeEnds?: boolean;
	applyCutsToProjectTime?: boolean;
	cutHandlingMode: CutHandlingMode;
	cutWarningThresholdDuration: number | null;
	automaticOvertimeCappingPeriod: AutomaticOvertimeCappingPeriod;
	automaticOvertimeCappingDuration: number;
	flexTimeIndicatorType?: FlexTimeIndicatorType;
	flexTimeIndicatorPositiveYellowThresholdDuration?: number;
	flexTimeIndicatorPositiveRedThresholdDuration?: number;
	flexTimeIndicatorPositiveYellowThresholdPercentage?: number;
	flexTimeIndicatorPositiveRedThresholdPercentage?: number;
	flexTimeIndicatorNegativeYellowThresholdDuration?: number;
	flexTimeIndicatorNegativeRedThresholdDuration?: number;
	flexTimeIndicatorNegativeYellowThresholdPercentage?: number;
	flexTimeIndicatorNegativeRedThresholdPercentage?: number;
	flexTimeIndicatorYellowEmailAdministrator: boolean;
	flexTimeIndicatorYellowEmailTeamLeader: boolean;
	flexTimeIndicatorYellowEmailUser: boolean;
	flexTimeIndicatorRedEmailAdministrator: boolean;
	flexTimeIndicatorRedEmailTeamLeader: boolean;
	flexTimeIndicatorRedEmailUser: boolean;
}

export interface WorkScheduleWeek {
	workDurationMonday: number;
	workDurationTuesday: number;
	workDurationWednesday: number;
	workDurationThursday: number;
	workDurationFriday: number;
	workDurationSaturday: number;
	workDurationSunday: number;
	normalWorkingTime1StartMonday?: string;
	normalWorkingTime1EndMonday?: string;
	normalWorkingTime2StartMonday?: string;
	normalWorkingTime2EndMonday?: string;
	normalWorkingTime1StartTuesday?: string;
	normalWorkingTime1EndTuesday?: string;
	normalWorkingTime2StartTuesday?: string;
	normalWorkingTime2EndTuesday?: string;
	normalWorkingTime1StartWednesday?: string;
	normalWorkingTime1EndWednesday?: string;
	normalWorkingTime2StartWednesday?: string;
	normalWorkingTime2EndWednesday?: string;
	normalWorkingTime1StartThursday?: string;
	normalWorkingTime1EndThursday?: string;
	normalWorkingTime2StartThursday?: string;
	normalWorkingTime2EndThursday?: string;
	normalWorkingTime1StartFriday?: string;
	normalWorkingTime1EndFriday?: string;
	normalWorkingTime2StartFriday?: string;
	normalWorkingTime2EndFriday?: string;
	normalWorkingTime1StartSaturday?: string;
	normalWorkingTime1EndSaturday?: string;
	normalWorkingTime2StartSaturday?: string;
	normalWorkingTime2EndSaturday?: string;
	normalWorkingTime1StartSunday?: string;
	normalWorkingTime1EndSunday?: string;
	normalWorkingTime2StartSunday?: string;
	normalWorkingTime2EndSunday?: string;
}

export interface WorkScheduleModel extends Identifiable {
	name: string;
	version: number;
	workScheduleModelType: WorkScheduleModelType;
	breakModelSelectionStatus: BreakModelSelectionStatus;
	breakModelId: number;
	targetWorkingTimeType?: TargetWorkingTimeType;
	workScheduleWeek: WorkScheduleWeek;
	/**
	 * Indicates whether custom vacation days are enabled for this work schedule model
	 */
	requiresCustomVacationEnabled: boolean;
	requiresCustomVacationOnMonday: boolean;
	requiresCustomVacationOnTuesday: boolean;
	requiresCustomVacationOnWednesday: boolean;
	requiresCustomVacationOnThursday: boolean;
	requiresCustomVacationOnFriday: boolean;
	requiresCustomVacationOnSaturday: boolean;
	requiresCustomVacationOnSunday: boolean;
	archived: boolean;
	projectTimeBookingRatio?: number;
	flexibleWorkDays: boolean;
	userCount?: number;
	workScheduleModelWeeks?: WorkScheduleModelWeek[] | null;
}

export interface WorkScheduleModelWeek extends Identifiable {
	name: string;
	weekNumber: number;
	workScheduleWeek: WorkScheduleWeek;
}

export interface Record {
	id: number;
	start: string;
	end: string;
	originalStart: string | null;
	originalEnd: string | null;
	user: User;
	changed: boolean;
	conflicted: boolean;
	breakTime: number;
	breakTimeManual: number;
	breakTimeAutomatic: number;
	durationGross: number;
	durationNet: number;
	durationNetRounded: number;
	/**
	 * @deprecated use durationGross instead
	 */
	duration: number;
	startPosition: GPSPosition;
	endPosition: GPSPosition;
	lastModified: string;
	version: number;
	revisionType: RevisionType;
	recordId: string;
	customField1: string;
	customField2: string;
	customField3: string;
	customField4: string;
	customField5: string;
	customField6: string;
	customField7: string;
	startPlatform: Platform;
	endPlatform: Platform;
}

export interface WorkingTime extends Record {
	workingTimeType: WorkingTimeType;
	description: string | null;
	status: RecordedEntryStatusKey;
	editUnit?: DurationUnit;
	durationDays?: number;
	deductible: number;
	compensationDeductible: number;
	breakTimePaid: number;
	durationLegalBase: number;
	hasTimeAccount: boolean;
	hasWorkingTimeRequest?: boolean;
	workingTimeDateSpanId?: number | null;
	workingTimeDateSpanEditable?: boolean | null;
	validationResults?: ValidationResult[];
}

export interface ProjectTime extends Record {
	task: Task;
	billable: boolean;
	description?: string;
	taskBreadCrumbs: string;
	status: RecordedEntryStatusKey;
	uploadFilesMetadata: AttachmentFileMetadata[];
	attachmentsToDelete: string[];
	attachmentCount?: number;
	hourlyRate?: number;
	hourlyRateInternal?: number;
	validationResults?: ValidationResult[];
	taskForGrouping?: Task;
}

export interface DriveLog extends Record {
	car?: Car;
	startMileage?: number;
	endMileage?: number;
	distance?: number;
	route?: string;
	purpose?: string;
	visited?: string;
	category?: DriveLogCategory;
	hasTrack?: boolean;
	status: RecordedEntryStatusKey;
	startVenue?: Venue;
	endVenue?: Venue;
}

/** Base fields shared by every report-table row (Java `ReportRecordCommonFields`, embedded via `@JsonUnwrapped`). */
export interface ReportRecord extends Identifiable {
	version: number;
	description: string | null;
	userName: string;
	userId: number;
	start: string;
	startTimezone: string;
	end: string;
	endTimezone: string;
	originalStartTime: string;
	originalStartTimeZone: string;
	originalEndTime: string;
	originalEndTimeZone: string;
	changed: boolean;
	startPosition: GPSPosition;
	endPosition: GPSPosition;
	breakTime: number;
	startPlatform: Platform;
	endPlatform: Platform;
	durationGross: number;
	durationNet: number;
	lastModified: string;
	lastModifiedTimezone: string;
	modifiedByUser: string;
	customField1: string;
	customField2: string;
	customField3: string;
	customField4: string;
	customField5: string;
	customField6: string;
	customField7: string;
	conflicted?: boolean;
}

/** Per-row working-time entity (Java `ReportWorkingTimeEntity`). */
export interface ReportWorkingTime extends ReportRecord {
	workingTimeTypeId: number;
	workingTimeTypeName: string;
	workingTimeTypeCategory: WorkingTimeTypeCategory;
	workingTimeTypeRecordingModeUser: WorkingTimeTypeRecordingMode;
	editUnit: DurationUnit;
	durationDays: number;
	breakTimeManual: number;
	breakTimeAutomatic: number;
	breakTimePaid: number;
	deductible: number;
	compensationDeductible: number;
	status: RecordedEntryStatus;
	hasTimeAccount?: boolean;
	workingTimeDateSpanId?: number | null;
	hasWorkingTimeRequest?: boolean;
	workingTimeDateSpanEditable?: boolean;
	/** Null when the validations haven't been lazy-loaded yet. */
	validations?: ReportValidations;
}

/** Per-row project-time entity (Java `ReportProjectTimeEntity`). */
export interface ReportProjectTime extends ReportRecord {
	taskId: number;
	taskName: string;
	taskBillable: boolean;
	taskDescription: string;
	taskBreadCrumbs: string;
	taskStart?: string;
	taskEnd?: string;
	isTaskLocked: boolean;
	customTaskField1: string;
	customTaskField2: string;
	customTaskField3: string;
	billable: boolean;
	status: RecordedEntryStatus;
	breakTimeManual: number;
	breakTimeAutomatic: number;
	/** Null when the validations haven't been lazy-loaded yet. */
	validations?: ReportValidations;
	attachments?: number;
}

/** Per-row drive-log entity (Java `ReportDriveLogEntity`). */
export interface ReportDriveLog extends ReportRecord {
	carId: number;
	plate: string;
	carName: string;
	route: string;
	purpose: string;
	visited: string;
	startMileage: number;
	endMileage: number | null;
	/** Difference between mileages; `null` for running drive logs where `endMileage` is not yet set. */
	distance: number | null;
	business: boolean;
	startVenue: string;
	endVenue: string;
	status: RecordedEntryStatus;
	hasTrack: boolean;
	category: ReportDriveLogCategory | null;
}

// ─── Report aggregate sums ─────────────────────────────────────────────────
// Mirror the Java POJOs WorkingTimeSums (extends SumsHolder), RecordStatistics,
// RecordSums, DriveLogStatistics. Jackson serialises via bean accessors —
// includes computed `breakTime` (manual + automatic) and `netDuration`
// (duration - breakTime * 60000).

/**
 * Mirrors Java {@code ReportValidations}.
 *
 * <p>TODO: Remove the relevant/irrelevant split in favour of nesting + runtime type, see TIMR-9318.
 */
export interface ReportValidations {
	relevant: ValidationResult[];
	irrelevant: ValidationResult[];
}

export interface SumsHolder {
	duration: number;
	breakTimeManual: number;
	breakTimeAutomatic: number;
	breakTime: number;
	netDuration: number;
}

export interface WorkingTimeSums extends SumsHolder {
	durationDays: number;
	durationGross: number;
}

export type RecordSums = SumsHolder;

export interface RecordStatistics {
	billableRecordSums: RecordSums;
	notBillableRecordSums: RecordSums;
}

// ─── Report wrapper responses ──────────────────────────────────────────────
// Mirror the Java sibling wrapper records under com.troii.timr.web.model.
// Revisions/conflicted/recording variants share the DataTable shape; only
// the `/reports/` variants carry an aggregate (workingTimeSums /
// projectTimeSums / driveLogSums). Top-level `validations` is present only
// when `Client.isUseSavedValidations()` is true.

/** Response of `/reports/getWorkingTimeRevisions.json` (Java `ReportWorkingTimeRevisionsResponse`). */
export interface ReportWorkingTimeRevisionsResponse {
	draw: number;
	data: ReportWorkingTime[];
	recordsFiltered: number;
	recordsTotal: number;
}

/** Response of `/reports/getProjectTimeRevisions.json` (Java `ReportProjectTimeRevisionsResponse`). */
export interface ReportProjectTimeRevisionsResponse {
	draw: number;
	data: ReportProjectTime[];
	recordsFiltered: number;
	recordsTotal: number;
}

/** Response of `/reports/getDriveLogRevisions.json` (Java `ReportDriveLogRevisionsResponse`). */
export interface ReportDriveLogRevisionsResponse {
	draw: number;
	data: ReportDriveLog[];
	recordsFiltered: number;
	recordsTotal: number;
}

/** Response of `/reports/getConflictedWorkingTimes.json` (Java `ConflictedWorkingTimesResponse`). */
export interface ConflictedWorkingTimesResponse {
	recordsReturned: number;
	conflictedWorkingTimes: ReportWorkingTime[];
}

/** Response of `/reports/getConflictedProjectTimes.json` (Java `ConflictedProjectTimesResponse`). */
export interface ConflictedProjectTimesResponse {
	recordsReturned: number;
	conflictedProjectTimes: ReportProjectTime[];
}

/** Response of `/reports/getConflictedDriveLogs.json` (Java `ConflictedDriveLogsResponse`). */
export interface ConflictedDriveLogsResponse {
	recordsReturned: number;
	conflictedDriveLogs: ReportDriveLog[];
}

/** Response of `/recording/getWorkingTimes.json` (Java `RecordingWorkingTimesResponse`). */
export interface RecordingWorkingTimesResponse {
	draw: number;
	data: ReportWorkingTime[];
	recordsFiltered: number;
	recordsTotal: number;
	validations?: ReportValidations;
}

/** Response of `/reports/getWorkingTimes.json` (Java `ReportWorkingTimesResponse`). */
export interface ReportWorkingTimesResponse {
	draw: number;
	data: ReportWorkingTime[];
	recordsFiltered: number;
	recordsTotal: number;
	workingTimeSums: WorkingTimeSums;
	validations?: ReportValidations;
}

/** Response of `/recording/getProjectTimes.json` (Java `RecordingProjectTimesResponse`). */
export interface RecordingProjectTimesResponse {
	draw: number;
	data: ReportProjectTime[];
	recordsFiltered: number;
	recordsTotal: number;
	validations?: ReportValidations;
}

/** Response of `/reports/getProjectTimes.json` (Java `ReportProjectTimesResponse`). */
export interface ReportProjectTimesResponse {
	draw: number;
	data: ReportProjectTime[];
	recordsFiltered: number;
	recordsTotal: number;
	projectTimeSums: RecordStatistics;
	validations?: ReportValidations;
}

/** Response of `/recording/getDriveLogs.json` (Java `RecordingDriveLogsResponse`). */
export interface RecordingDriveLogsResponse {
	draw: number;
	data: ReportDriveLog[];
	recordsFiltered: number;
	recordsTotal: number;
}

/** Response of `/reports/getDriveLogs.json` (Java `ReportDriveLogsResponse`). */
export interface ReportDriveLogsResponse {
	draw: number;
	data: ReportDriveLog[];
	recordsFiltered: number;
	recordsTotal: number;
	driveLogSums: DriveLogUserSums;
}

export interface Venue extends Identifiable {
	version: number;
	name: string;
	city: string;
	zipCode: string;
	street: string;
	streetNumber: string;
	country: string;
	countryCode: string;
	latitude: number;
	longitude: number;
	venueId: string;
}

export interface RunningEntry {
	id: number;
	recordId: string;
	version: number;
	changed: boolean;
	user: User;
	description?: string;
	startPosition?: GPSPosition;
	start: string;
	startPlatform: Platform;
	pausedAt?: string;
	status: RunningTimeStatus;
	breakTime?: number;
	customField1: string;
	customField2: string;
	customField3: string;
	customField4: string;
	customField5: string;
	customField6: string;
	customField7: string;
}

export interface RunningWorkingTime extends RunningEntry {
	workingTimeType: WorkingTimeType;
}

export interface RunningProjectTime extends RunningEntry {
	attachmentCount: number;
	taskBreadCrumbs: string;
	task: Task;
	billable: boolean;
}

export interface RunningDriveLog extends RunningEntry {
	car: Car;
	category: DriveLogCategory;
	route?: string;
	purpose?: string;
	visited?: string;
	startMileage?: number;
	endMileage?: number;
}

export interface DriveLogCategory {
	id: number;
	name: string;
	business: boolean;
	purposeRequired: boolean;
	color: number;
	archived: boolean;
	categoryId: string;
	version: number;
}

export interface ReportDriveLogCategory {
	id: number;
	name: string;
	business: boolean;
	color: number;
	archived: boolean;
}

export interface CustomFieldDefinition {
	fieldNumber: 1 | 2 | 3 | 4 | 5 | 6 | 7;
	fieldName: string;
	fieldType: CustomFieldsForRecordType;
	mandatory: boolean;
	optionsList?: string[];
}

/** Item shape for custom-field option Comboboxes — values are stored as strings. */
export interface CustomFieldOption {
	id: string;
}

export interface GPSPosition {
	id: number;
	longitude: number;
	latitude: number;
	timestamp?: string;
	horizontalAccuracy: number;
	verticalAccuracy: number;
	formattedAddress: string;
	deletedStatus: DeletedStatus;
}

export interface ValidationResult extends Identifiable {
	/** ISO date string "yyyy-MM-dd", serialized from Java {@code LocalDate} */
	day: string;
	weekday: string;
	user: ValidationResultUser;
	recordedEntry: ValidationResultRecord;
	recordedEntryRef: ValidationResultRecord;
	severity: ValidationSeverity;
	status: RecordingValidationStatus;
	validationId: number;
	messageCode: string;
	messageArguments: string[];
	allowedTargetStates: RecordingValidationStatus[];
	category: RecordingValidationCategory;
}

export type RecordingValidationCategory = WorkingTimeValidationCategory | ProjectTimeValidationCategory;

export interface ValidationData {
	valid: boolean;
	balancesCount: number;
	validationMessage: ValidationMessage[];
}

export interface WorkingTimeStatus {
	runningWorkingTime: RunningWorkingTime;
	lastWorkingTime: WorkingTime;
	workingTimeUserSums: UserSumsWorkingTime;
	targetWorkDurations: WorkDurations;
}

export interface ProjectTimeStatus {
	runningProjectTime: RunningProjectTime;
	lastProjectTime: ProjectTime;
	projectTimeUserSums: UserSumsProjectTime;
}

export interface UserSumsProjectTime {
	durationDay: number;
	durationWeek: number;
	durationMonth: number;
	durationBillableDay: number;
	durationNotBillableDay: number;
	durationBillableWeek: number;
	durationNotBillableWeek: number;
	durationBillableMonth: number;
	durationNotBillableMonth: number;
}

export interface DriveLogSums {
	durationNet: number;
	distance: number;
}

export interface DriveLogUserSums {
	businessSums: DriveLogSums;
	privateSums: DriveLogSums;
}

/** `workingTimeSums` field in the working-time table response. */
export interface WorkingTimeTableSums {
	/** Milliseconds. */
	duration: number;
	/** Milliseconds. */
	durationGross: number;
	/** Minutes. */
	breakTime: number;
}

/** Per-billability sums in the project-time table response. */
export interface ProjectTimeRecordSums {
	/** Milliseconds. */
	duration: number;
	/** Minutes. */
	breakTime: number;
}

/** `projectTimeSums` field in the project-time table response. */
export interface ProjectTimeTableSums {
	billableRecordSums: ProjectTimeRecordSums;
	notBillableRecordSums: ProjectTimeRecordSums;
}

export interface DriveLogStatus {
	runningDriveLog: RunningDriveLog;
	lastDriveLog: DriveLog;
	driveLogUserSums: DriveLogUserSums;
	carList: Car[];
}

export interface ValidationResultUser extends Identifiable {
	firstname: string;
	lastname: string;
	fullname: string;
}

export interface ValidationResultRecord extends Identifiable {
	type?: ValidationResultRecordType | null;
	start: string;
	end: string;
	user: {
		id: number,
		firstname: string,
		lastname: string,
		fullname: string,
		trustBasedWorkingTime: boolean
	};
	task?: {
		id: number,
		name: string,
		breadCrumbs?: string
	};
	workingTimeType?: {
		id: number,
		name: string,
	};
}

export type ValidationResultRecordType = "workingTime" | "projectTime";

// TODO: Document when this is used over ValidationResult
export interface RecordingValidationMessage extends Identifiable {
	id: number;
	/** Client-locale date string formatted via {@code DateTimeUtil.formatDate(LocalDate, client.getDateFormat())} */
	date: string;
	weekday: string;
	message: string;
	messageCode: string;
	messageArguments: string[];
	severity: ValidationSeverity;
	userId: number;
	/**
	 * user.fullname actually
	 */
	user: string;
	recordedEntry?: Record;
	recordedEntryRef?: Record;
	status: RecordingValidationStatus;
}

export interface PartialWorkingTimeRequest {
	id: number;
	workingTimeRequestId: string;
	version: number;
	user: PartialUser;
	workingTimeType: PartialWorkingTimeType;
	description: string;
	start: string;
	end: string;
	startHalfDay: boolean;
	endHalfDay: boolean;
	status: WorkingTimeRequestStatus;
	statusComment: string;
	substitute?: PartialUser;
	requiresSubstitute: boolean;
	/**
	 * The current workflow step that is pending approval
	 * and can be approved by the current user.
	 * If null, the current user is not allowed to approve
	 * the request.
	 */
	currentApprovableStep?: WorkingTimeRequestStep;
	/**
	 * The approval process steps of the request.
	 */
	approvalSteps?: WorkingTimeRequestStep[];
	/**
	 * The newest cancellation process steps of the request.
	 */
	cancellationSteps?: WorkingTimeRequestStep[];
	proofRequirement: ProofRequirement;
	proofRequiredAfterDays: number | null;
	proofId: number | null;
	proofStatus: ProofStatus | null;
}

export interface AbstractWorkingTimeRequestResponseModel {
	type: "MINUTE_BASED" | "DATE_SPAN" | "NOTICE";
	id: number;
	version: number;
	start: string;
	end: string;
	workingTimeRequestId: string;
	user: PartialUser;
	workingTimeType: PartialWorkingTimeType;
	description: string | null;
	status: WorkingTimeRequestStatus;
	statusComment: string | null;
	created: string | null;
	lastModified: string | null;
	modifiedByUser: PartialUser | null;
	approvalSteps: WorkingTimeRequestStep[] | null;
	cancellationSteps: WorkingTimeRequestStep[] | null;
	currentApprovableStep: WorkingTimeRequestStep | null;
	proofRequirement: ProofRequirement;
	proofRequiredAfterDays: number | null;
	proofId: number | null;
	proofStatus: ProofStatus | null;
}

export interface WorkingTimeRequestResponseModel extends AbstractWorkingTimeRequestResponseModel {
	breakTimeManual: number;
	customField1: string | null;
	customField2: string | null;
	customField3: string | null;
	customField4: string | null;
	customField5: string | null;
	customField6: string | null;
	customField7: string | null;
}

export interface WorkingTimeDateSpanRequestResponseModel extends AbstractWorkingTimeRequestResponseModel {
	startHalfDay: boolean;
	endHalfDay: boolean;
	substitute: PartialUser | null;
	requiresSubstitute: boolean;
}

export interface WorkingTimeNoticeRequestResponseModel extends AbstractWorkingTimeRequestResponseModel {
	type: "NOTICE";
}

export interface WorkingTimeDateSpanRequest {
	version: number;
	id: number;
	workingTimeRequestId: string;
	user: User;
	workingTimeType: WorkingTimeType;
	description: string;
	start: string;
	end: string;
	startHalfDay: boolean;
	endHalfDay: boolean;
	status: WorkingTimeRequestStatus;
	statusComment: string;
	substitute?: User;
	requiresSubstitute: boolean;
	created: string;
	lastModified: string;
	modifiedByUser: User | null;
	approvalSteps: WorkingTimeRequestStep[];
	cancellationSteps: WorkingTimeRequestStep[];
	proofRequirement: ProofRequirement;
	proofRequiredAfterDays: number | null;
	proofId: number | null;
	proofStatus: ProofStatus | null;
}

export interface Holiday {
	version: number;
	id: number;
	description: string;
	date: string;
	halfDay?: boolean;
	holidayCalendar?: HolidayCalendar;
}

export interface HolidayCalendar {
	version: number;
	id: number;
	description: string;
	calculator: string;
	generationMode: HolidayCalendarGenerationMode;
	individualChristmasEve: HolidayDayDuration;
	individualSilvester: HolidayDayDuration;
}

export interface Absence {
	userId: number;
	userFullname: string;
	startDate: string;
	endDate: string;
	startHalfDay: boolean;
	endHalfDay: boolean;
	pending: boolean;
	substituteFullname?: string;
	editUnit: DurationUnit;
	start?: string;
	end?: string;
}

export interface HolidayAbsence {
	userId: number;
	userFullname: string;
	holidayId: number;
	date: string;
	halfDay: boolean;
	holidayDescription: string;
	holidayCalendarDescription: string;
}

export interface TreeNode {
	title: string;
	lazy: boolean;
	key: string;
	numberOfChildren: number;
	assignedToCurrentUser: boolean;
}

export interface AuthToken {
	uuid: string;
}

export interface TerminalConfig {
	id: number;
	name: string;
	token: string;
	qrCodeAuthentication: boolean;
	timeoutDuration: number;
	useDefaultLocationRequirements: boolean;
	geofenceEnabled: boolean;
	defaultTask?: Task;
	defaultWorkingTimeType?: WorkingTimeType;
	taskSelectionTypes: MobileTaskSelectionType[];
	taskSelectionOpenSearchInAll: boolean;
	taskSelectionStartOnSelection: boolean;
	taskSelectionOpenAutomatically: boolean;
	useMobileTaskSelectionTypes: boolean;
}

export interface OvertimeLimit {
	id: number;
	name: string;
	duration: number;
	breakModelId?: number;
}

export interface OvertimeRule {
	id: number;
	version: number;
	name: string;
	shortName: string;
	ruleCategory: OvertimeRuleCategory;
	ruleType: OvertimeRuleType;
	percentage: number;
	monday: boolean;
	tuesday: boolean;
	wednesday: boolean;
	thursday: boolean;
	friday: boolean;
	saturday: boolean;
	sunday: boolean;
	holiday: boolean;
	timeFrom?: string;
	timeTo?: string;
	durationOver?: number;
	active: boolean;
	initiated: boolean;
}

/** Mirrors Java: LoadOvertimeLimitsResponse */
export interface LoadOvertimeLimitsResponse {
	draw: number;
	overtimeLimits: OvertimeLimit[];
	recordsFiltered: number;
	recordsTotal: number;
}

/** Mirrors Java: GetOvertimeRulesResponse.OvertimeRuleEntry */
export interface OvertimeRuleEntry {
	id: number;
	name: string;
	percentage: number;
	/** Omitted from the JSON for dummy rules (field-level @JsonInclude(NON_NULL) on the backend) */
	shortName?: string;
	/** Omitted from the JSON for dummy rules (field-level @JsonInclude(NON_NULL) on the backend) */
	ruleCategory?: OvertimeRuleCategory;
}

/** Mirrors Java: GetOvertimeRulesResponse */
export interface GetOvertimeRulesResponse {
	overtimeRules: OvertimeRuleEntry[];
}

/** Mirrors Java: com.troii.timr.web.model.ValidationMessage */
export interface ValidationMessage {
	id: number;
	date: string;
	weekday: string;
	message: string;
	messageCode: string;
	userId: number;
	user: string;
	recordedEntry: Record | null;
	recordedEntryRef: Record | null;
	status: string | null;
}

/** Mirrors Java: MandatoryValidationsResponse */
export interface MandatoryValidationsResponse {
	valid: boolean;
	validationMessage: ValidationMessage[];
}

/** Mirrors Java: CalculateVacationResponse (sealed interface with WithAccount / WithoutAccount subtypes) */
export interface CalculateVacationResponse {
	vacationRequired: number;
	vacationUnit: VacationUnit;
	warningText: string | null;
	vacationAccount: boolean;
	/**
	 * Three states preserved from the original wire format:
	 *   - undefined (field absent): vacationAccount is false
	 *   - null:                     vacationAccount is true but the user's current balance is unknown
	 *   - number:                   vacationAccount is true and the balance is known
	 */
	vacationLeft?: number | null;
}

export type OvertimeRuleType =
	"WHOLE_DAY"
	| "TIME_RANGE"
	| "DAILY_TARGET_DURATION_OVER_AND_TIME_RANGE"
	| "DAILY_TARGET_WORK_DURATION_OVER"
	| "DAILY_DURATION_OVER"
	| "DAILY_DURATION_OVER_AND_TIME_RANGE"
	| "WEEKLY_DURATION_OVER"
	| "WEEKLY_TARGET_WORK_DURATION_OVER";

export interface TimeZoneId {
	id: string; // corresponds to zoneId.id see https://docs.oracle.com/javase/8/docs/api/java/time/ZoneId.html#getId
	offset: string;
	displayName: string;
}

export type OvertimeRuleCategory = "OVERTIME" | "ALLOWANCE";

/**
 * IMPORTANT Update the java file too: TimeAccountDashboardBalanceMode.java
 */
export type TimeAccountDashboardBalanceMode = "YESTERDAY" | "TODAY";

/**
 * IMPORTANT Update the java file too: WorkBalanceCorrectionType.java
 */
export type WorkBalanceCorrectionType = "PAYOFF" | "CORRECTION" | "TRANSFER";

/**
 * IMPORTANT Update the java file too: TimeAccountViewType.java
 */
export type TimeAccountViewType = "MY_ACCOUNT" | "CURRENT_BALANCES" | "ALL_ACCOUNTS";

/**
 * Equivalent to DateTimeChangeableResult.java
 */
export type DateTimeChangeableResult =
	"ALLOWED"
	| "NOT_ALLOWED"
	| "NOT_ALLOWED_PREVENT_EDIT"
	| "ALLOWED_WARNING"
	| "NOT_ALLOWED_FUTURE";

export interface PresenceBoardBaseUser {
	presenceBoardUserType: PresenceBoardCategory;
	fullName: string;
	groups?: string;
}

export interface NowActiveUser extends PresenceBoardBaseUser {
	workingTimeTypeName?: string;
	workingTimeTypeSubCategory: WorkingTimeTypeSubCategory;
	paused: boolean;
	taskName?: string;
	taskBreadcrumbs?: string;
	projectTimeBillable?: boolean;
}

export interface InactiveUser extends PresenceBoardBaseUser {
	wasActiveToday: boolean;
}

export interface HolidayUser extends PresenceBoardBaseUser {
	holidayDescription: string;
}

export interface NoWorkDayUser extends PresenceBoardBaseUser {
	workingDays: string[];
}

export interface AbsentUser extends PresenceBoardBaseUser {
	end: string;
	substitute: PartialUser;
}

export interface ApiErrorResponse {
	fieldErrors?: FieldError[];
	code?: string;
	message?: string;
	stackTrace?: string;
}

export interface ErrorResponse {
	responseJSON?: ApiErrorResponse;
}

export interface FieldError {
	code?: string;
	field: string;
	message: string;
	resource?: string;
}


/**
 * Partial view of the response from POST /account/saveBillingSettings.json.
 *
 * The backend (AccountSettingsController.saveBillingSettings) returns a JSONAddressResponse
 * that also includes company, address, vatPercentage and netPayment, none of which this
 * call site consumes.
 */
export interface SaveBillingSettingsResponse {
	vatNumber?: string;
	vatValid?: boolean;
}

/** Mirrors Java {@code PresenceBoardController.PresenceBoardDetails}. */
export interface PresenceBoardResponse {
	data: PresenceBoardBaseUser[];
	showProjectTimeInPresenceBoard: boolean;
	securityStatus: SecurityStatusDTO;
	presenceBoardVisibility: PresenceBoardVisibility;
}

export interface StartEndTime {
	start: string;
	end: string;
}

export interface OAuthClient {
	id: number;
	oAuthClientId: string;
	oAuthClientName: string;
	oAuthClientIdIssuedAt: string;
	oAuthClientSecret: string;
	authorizationGrantTypes: string;
	redirectUris: string;
	scopes: string;
}

export interface Integration {
	id: number;
	type: IntegrationType;
	configuration: unknown;
	enabled: boolean;
	eventTypes: string[];
	multiInstance: boolean;
}

export interface TaskDetailsResponse {
	task: Task;
	hoursPlannedForSubtasks: number;
	budgetPlannedForSubtasks: number;
}

export interface SaveTaskResponse {
	task: Task;
	securityStatus: SecurityStatusDTO;
	attachmentUploadUrls: AttachmentUploadUrlResponse[];
}

/** Response from /tasks/getRecommendedTasks.json */
export interface RecommendedTasksResponse {
	recommendedTasks: Task[];
	scores: number[];
	recentTasks: Task[];
}

/** Mirrors Java {@code AddressDTO}. (Distinct from {@link Address}.) */
export interface AddressDTO {
	id?: number;
	address?: string;
	city?: string;
	zipCode?: string;
	state?: string;
	country?: CountryDTO;
}

/** Mirrors Java {@code CountryDTO}. */
export interface CountryDTO {
	id?: number;
	name?: string;
	code?: string;
	/** ISO date string "yyyy-MM-dd". */
	inEUSince?: string | null;
}

/** Response from `/account/saveBillingSettings.json`. Mirrors Java {@code JSONAddressResponse}. */
export interface BillingSettingsResponse {
	company?: string;
	address?: AddressDTO;
	vatNumber?: string;
	vatValid?: boolean;
	vatPercentage?: number;
	netPayment?: boolean;
}

/** Mirrors Java {@code BackgroundJobStatus}. */
export type BackgroundJobStatus = "WAITING" | "RUNNING" | "SUCCESS" | "ERROR" | "CANCELLED";

/** Mirrors Java {@code Tab}; these values are submitted by the client-settings form. */
export enum PrimaryTab {
	WorkingTime = "WORKINGTIME",
	ProjectTime = "PROJECTTIME",
	DriveLog = "DRIVELOG",
	Automatically = "AUTOMATICALLY"
}

/**
 * Response from `POST /admin/saveClientSettings.json` and
 * `GET /admin/reloadSettingsBackgroundjobsStatus.json`. No Java DTO exists;
 * both endpoints emit the `HashMap<String, Object>` from
 * `ClientSettingsController.createSettingsModel` which also feeds
 * `/admin/settings.html`'s JSP render. The `jsConstants` sub-bag is bootstrapped
 * via the JSP `<script>` tag and not consumed from this JSON response.
 */
export interface ClientSettingsModelResponse {
	logoAvailable: boolean;
	workingTimeStatus: BackgroundJobStatus | null;
	workingTimeOverlapsStatus: BackgroundJobStatus | null;
	projectTimeOverlapsStatus: BackgroundJobStatus | null;
	projectTimeExistsWorkingTimesStatus: BackgroundJobStatus | null;
	isDisableBrandingAllowed: boolean;
	googleWorkplaceDirectedConnected?: boolean;
	microsoftGraphConnected?: boolean;
	currentUserSSOConnections?: string[];
	samlconfigAvailable: boolean;
	samlconfigProvider?: string;
	samlconfigMetadata?: string;
	availableSSO: string[];
	availableIntegrations: string[];
	passwordStrengthValidationConfigUseCustom: boolean;
	passwordStrengthValidationConfigMinimumLength?: number;
	passwordStrengthValidationConfigRequireUppercase?: boolean;
	passwordStrengthValidationConfigRequireLowercase?: boolean;
	passwordStrengthValidationConfigRequireSpecial?: boolean;
	passwordStrengthValidationConfigRequireDigit?: boolean;
	validRemoteAddresses: string[];
}

/** Response from /reports/getRecordingValidationsCurrentlyGeneratedMessage.json */
export interface CurrentlyGeneratedMessageResponse {
	message: string;
}

/**
 * Response from `/pricing.json` — the public pricing endpoint. Built by
 * `OpenPricingController.buildPricingMap`, which defaults `activeFeatureModules`
 * to the first base module. Distinct from {@link AvailableFeatureModulesResponse},
 * which omits `activeFeatureModules` entirely.
 */
export interface PricingResponse {
	baseFeatureModules: FeatureModule[];
	extensionFeatureModules: FeatureModule[];
	activeFeatureModules: FeatureModule[];
}

/** Mirrors Java {@code BmdIntegrationController.BmdAbsenceTypeDetails}. */
export interface BmdAbsenceTypeDetails {
	key: string;
	number: number;
	name: string;
}

/** Mirrors Java {@code BmdIntegrationController.DayBasedWorkingTimeTypeDetails}. */
export interface DayBasedWorkingTimeTypeDetails {
	id: number;
	name: string;
}

export interface TaskBudgetInfo {
	taskId: number;
	budgetCountNotBillableHours: boolean;
	budgetPlanningType: BudgetPlanningType;
	hoursPlanned: number;
	duration: number;
	breakTime: number;
	breakTimeManual: number;
	breakTimeAutomatic: number;
}

export interface TaskUserBudgetInfo extends TaskBudgetInfo {
	userId: number;
	projectBudget: boolean;
	userHourlyRate: number;
}

/** Mirrors Java: CheckForWorkScheduleModelChangeResponse */
export interface CheckForWorkScheduleModelChangeResponse {
	workDaysChanged: boolean;
	workingTimesForRequests: number;
	changeableWorkingTimesForRequests: number;
	workingTimesForWorkScheduleModel: number;
	workingTimesForBreakModel: number;
	cutWorkingTimesConfirm: boolean;
	overlappingWorkingTimeRequestsCount: number;
	overlappingWorkingTimeRequests: WorkingTimeDateSpanRequest[];
	overlappingWorkingTimeDateSpansCount: number;
	overlappingWorkingTimeDateSpans: WorkingTimeDateSpan[];
	workingTimeDateSpansAfterLastTimeAccountBalance: number;
}

/** Mirrors Java: ValidateTimeAccountBalanceResponse */
export interface ValidateTimeAccountBalanceResponse {
	valid: boolean;
	balancesCount: number;
	validationMessage: ValidationMessage[];
}

export interface WorkingTimeBulkOperationResult extends BulkOperationResult {
	balanced: number;
	createdByRequestOrDateSpan: number;
}

export interface ProjectTimeBulkOperationResult extends BulkOperationResult {
	taskLocked: number;
	taskNotAssigned: number;
}

export interface BulkOperationResult {
	updated: number;
	locked: number;
	insufficientRights: number;
	total: number;
	allUpdated: boolean;
}

export interface ExportResponse {
	exportJobId: string;
}

/** Analysis domain a Team Insights recommendation belongs to — aligned with the three analysis agents. */
export type TeamInsightsActionItemCategory = "VACATION_AND_ABSENCE" | "WORKTIME_VALIDATIONS" | "TIME_ACCOUNT";

export type TeamInsightsDataQuality = "CONSISTENT" | "INCONSISTENT";

export type TeamInsightsRecommendedAction =
	| "CLARIFY_WITH_EMPLOYEE"
	| "CHECK_WITH_HR_OR_POLICY"
	| "VERIFY_SYSTEM_OR_DATA"
	| "TAKE_PLANNING_ACTION"
	| "OTHER";

/** One Team Insights recommendation about a single employee. */
export interface TeamInsightsActionItem {
	id: string;
	analysisPeriod: string;
	title: string;
	summary: string;
	suggestedActions: string[];
	citations: string[];
	category: TeamInsightsActionItemCategory;
	dataQuality: TeamInsightsDataQuality;
	recommendedAction: TeamInsightsRecommendedAction;
	severity: number;
}

/** The Team Insights recommendations payload shape nested on each user result. */
export interface TeamInsightsRecommendationsPayload {
	items: TeamInsightsActionItem[];
}

export type TeamInsightsResultStatus = "RUNNING" | "SUCCESS" | "FAILED";

/** Per-user Team Insights result returned by the latest-run endpoints. */
export interface TeamInsightsUserResultResponse {
	resultId: number;
	userId: number;
	userName: string;
	status: TeamInsightsResultStatus;
	recommendations: TeamInsightsRecommendationsPayload | null;
}

export type TeamInsightsRunStatus = "RUNNING" | "DONE";

/**
 * The synthesized Team Insights latest run. There is no run entity on the backend — status,
 * referenceDate and created are derived from the latest per-user results.
 */
export interface TeamInsightsRunResponse {
	status: TeamInsightsRunStatus;
	referenceDate: string;
	/** ISO-8601 instant, e.g. "2026-07-13T08:34:49Z" (serialized from a backend java.time.Instant). */
	created: string;
	results: TeamInsightsUserResultResponse[];
}

export interface TeamInsightsFeedbackRequest {
	resultId: number;
	itemId: string;
	positive: boolean;
}

export interface FeatureModule {
	id: number;
	name: string;
	description: string;
	basePrice: number;
	pricePerUser: number;
	currency: string;
	status: FeatureModuleStatus;
	fixedNumberOfUsers: boolean | null;
	trialUntil: string;
	icon?: string;
	features: TimrFeature[];
	marketingTexts?: MarketingText[] | null;
	active?: boolean;
	individualOnly?: boolean;
	addOns?: FeatureModule[];
	dependsOn?: FeatureModule;
}

export interface MarketingText {
	group: string;
	features: MarketingTextFeature[];
}

export interface MarketingTextFeature {
	name: string;
	tooltip?: string;
}

export interface FeatureModulePriceCalculationUpgradeFees {
	featureModuleUpgradeFee: number;
	userUpgradeFee: number;
	runtimeExtensionFee: number;
	upgradeRebateDiscount: number;
	upgradeUserVolumeDiscount: number;
}

export interface FeatureModulePriceCalculationSummary {
	users: number;
	months: number;
	basePrice: number;
	pricePerUser: number;
	monthlyFee: number;
	userVolumeDiscount: number;
	userVolumeDiscountPercentage: number;
	rebateAmount: number;
	discountedMonthlyFee: number;
	totalFee: number;
	runtimeDiscount: number;
	runtimeDiscountPercentage: number;
	totalAmountBeforeVAT: number;
	amountVAT: number;
	amountPayable: number;
}

export interface FeatureModulePriceCalculationUpgradeSummary {
	monthsBetween: number;
	totalAmountBeforeVAT: number;
	amountVAT: number;
	amountPayable: number;
	upgradeFees: FeatureModulePriceCalculationUpgradeFees;
}

export interface FeatureModulesPriceCalculationResult {
	totalAmountPayable?: number;
	totalAmountBeforeVAT: number;
	totalAmountVAT?: number;
	vatPercentage?: number;
	summary: FeatureModulePriceCalculationSummary;
	appliedDiscount?: number;
	upgradeSummary: FeatureModulePriceCalculationUpgradeSummary;
	upgrade: boolean;
}

export interface PayableMonths {
	months: number;
	discountPercentage: number;
}

export interface AbsenceTimeRange {
	userFullname: string;
	start: string;
	end: string;
	isPending: boolean;
}

export interface WorkingTimeRange {
	startDate: string;
	endDate: string;
	startTime?: string;
	endTime?: string;
	workingTimeTypeName: string;
}

/**
 * Mirrors Java {@code dashboard.BudgetSumEntry}. Row shape of the Budget Task
 * and Budget User dashboards (Java wire model `BudgetSumMapResponse`).
 * Distinct from {@link DashboardUserEntry}, which is the row for every
 * non-budget dashboard.
 */
export interface DashboardBudgetSumEntry {
	id: number;
	description: string;
	hoursPlanned?: number;
	hoursPlannedProjectBudget?: number;
	remaining?: number;
	remainingProjectBudget?: number;
	durationForBudget: number;
	myDurationForBudget: number;
	durationBillable: number;
	durationNotBillable: number;
	duration: number;
	durationNotPlanned: number;
	budgetPlanned?: number;
	budgetPlannedProject: number;
	amountForBudget: number;
	amountForProjectBudget: number;
	myAmountForBudget: number;
	amountBillable: number;
	amountNotBillable: number;
	amount: number;
	amountInternal: number;
	amountInternalProjectBudget: number;
	myAmountInternalForBudget: number;
	amountRemaining: number;
	amountRemainingProjectBudget: number;
	amountRemainingInternal: number;
	amountRemainingInternalProjectBudget: number;
	showHourlyRateWarning: boolean;
	showHourlyRateInternalWarning: boolean;
	hourlyRateDefinedForAllUsers: boolean;
	hourlyRateInternalDefinedForAllUsers: boolean;
}

/**
 * Mirrors Java {@code dashboard.UserEntry}. Row shape of every non-budget
 * dashboard (Task / User / UserTask / WorkingTimeType — all Java wire model
 * `UserMapResponse`), despite the Java name suggesting "user". The budget
 * dashboards use {@link DashboardBudgetSumEntry} instead.
 */
export interface DashboardUserEntry {
	id: number;
	description: string;
	duration: number;
	durationBillable: number;
	durationNotBillable: number;
	workingTimeDuration: number;
	category?: string;
	statuses?: DashboardStatusEntry[];
	categories?: WorkingTimeCategory[];
	workingTimeStatuses?: DashboardStatusEntry[];
	workingTimeCategories?: WorkingTimeCategory[];
}

export interface WorkingTimeCategory {
	category: string;
	duration: number;
}

/** Mirrors Java {@code dashboard.StatusEntry}. */
export interface DashboardStatusEntry {
	status: number;
	duration: number;
}

/**
 * Structural shape consumed by {@link DashboardTable.renderDetailsPopover}.
 * Both {@link DashboardUserEntry} and {@link DashboardBudgetSumEntry} are
 * assignable; fields are optional because only a subset is present depending
 * on which dashboard is rendering.
 */
export interface DashboardDetailsEntry {
	workingTimeCategories?: WorkingTimeCategory[];
	workingTimeDuration?: number;
	duration?: number;
	durationBillable?: number;
	durationNotBillable?: number;
	statuses?: DashboardStatusEntry[];
}

export interface DashboardPathEntry {
	id: number;
	taskName: string;
}

export interface TaskDashboardResponse {
	id?: number;
	description?: string;
	path?: DashboardPathEntry[];
	tasks: {
		results: DashboardUserEntry[];
		total: DashboardUserEntry;
	};
}

export interface UserDashboardResponse {
	id?: number;
	description?: string;
	users: {
		results: DashboardUserEntry[];
		total: DashboardUserEntry;
	};
}

/** Response of `/reports/getUsersForTaskDashboard.json` (Java `UserTaskDashboardResponse`). */
export interface UserTaskDashboardResponse {
	users: {
		results: DashboardUserEntry[];
		total: DashboardUserEntry;
	};
}

/** Response of `/reports/getTasksForBudgetDashboard.json` (Java `BudgetDashboardTaskResponse`). */
export interface BudgetDashboardTaskResponse {
	id?: number;
	description?: string;
	path?: DashboardPathEntry[];
	taskExternalComments?: string;
	tasks: {
		results: DashboardBudgetSumEntry[];
		total: DashboardBudgetSumEntry;
	};
}

/** Response of `/reports/getUsersForBudgetDashboard.json` (Java `BudgetDashboardUserResponse`). */
export interface BudgetDashboardUserResponse {
	users: {
		results: DashboardBudgetSumEntry[];
		total: DashboardBudgetSumEntry;
	};
}

/** Response of `/reports/getRunningWorkingTimes.json` (Java `RunningWorkingTimesResponse`). */
export interface RunningWorkingTimesResponse {
	workingTimes: RunningWorkingTime[];
}

/** Response of `/reports/getRunningProjectTimes.json` (Java `RunningProjectTimesResponse`). */
export interface RunningProjectTimesResponse {
	projectTimes: RunningProjectTime[];
}

/** Response of `/reports/getRunningDriveLogs.json` (Java `RunningDriveLogsResponse`). */
export interface RunningDriveLogsResponse {
	driveLogs: RunningDriveLog[];
}

/**
 * {@link DashboardUserEntry} (the wire row) augmented with `durationScale` for
 * bar rendering. `durationScale` is **not** on the wire — it is computed and
 * mutated onto the row in `WorkingTimeTypeTable.onDataChanged`.
 */
export interface WorkingTimeTypeDashboardEntry extends DashboardUserEntry {
	durationScale?: number;
}

export interface WorkingTimeTypeDashboardResponse {
	workingTimeTypes: {
		results: WorkingTimeTypeDashboardEntry[];
		total: WorkingTimeTypeDashboardEntry;
	};
}

export interface AttachmentFileMetadata {
	contentType: string;
	fileName: string;
	size: number;
	/** ISO-8601 instant, e.g. "2026-07-13T08:34:49Z" (serialized from a backend java.time.Instant). */
	fileLastModified: string;
}

export interface AttachmentUploadUrlResponse {
	url: string;
	signedHeaders: { [key: string]: string[] };
	attachmentId: string;
	entityId: number;
}

export interface AttachmentDownloadUrlResponse {
	url: string;
	metadata: AttachmentFileMetadata
	attachmentId: string;
}

export interface TaskAttachmentDownloadUrlResponse {
	url: string;
	metadata: AttachmentFileMetadata
	attachmentId: string;
	inheritedFromTaskId?: number
}

export interface WorkingTimeRequestStep {
	step: number;
	workingTimeRequestStepId: string;
	type: WorkingTimeRequestStepType;
	requiredApproverType: WorkingTimeRequestStepConfigurationType;
	requiredApproverUser?: PartialUser;
	status?: WorkingTimeRequestStepStatus;
	comment: string | null;
	/** ISO-8601 instant, e.g. "2026-07-13T08:34:49Z" (serialized from a backend java.time.Instant). */
	completedAt: string | null;
	completedByUser: PartialUser | null;
	proofReviewAction: ProofReviewAction | null;
}

export interface TimeAccountData {
	lastOvertimeDurationTotalBalance: number;
	overtimeDurationCurrentBalance: number;
	overtimeDurationCurrentTotalBalance: number;
	lastAllowanceDurationTotalBalance: number;
	allowanceDurationCurrentBalance: number;
	allowanceDurationCurrentTotalBalance: number;
	lastWorkDurationTotalBalance: number;
	workDurationCurrentBalance: number;
	workDurationCurrentTotalBalance: number;
	lastTimeAccountBalanceDate: string;
	flexTimeIndicator: FlexTimeIndicator;
}

export interface TimeAccountVacationData extends TimeAccountData, TimeAccountOvertimeData, TimeAccountTravellingTimeData {
	isInitialized: boolean;
	lastVacationTotalBalance: number;
	exitDate: string | null;
	workDurationCurrentTarget: number;
	workDurationCurrentActual: number;
	unpaidTimeOffDurationPlanned: number;
	vacationYearFrom: string;
	vacationYearTo: string;
	vacationConsumed: number;
	vacationPlanned: number;
	vacationLeft: number;
	vacationUnit: VacationUnit;
	validationMessage?: ValidationMessage[];
	addOvertimes: boolean;
	addTravellingTimes: boolean;
	trustBasedWorkingTime: boolean;
	vacationAccount: boolean;
	workingTimeTracking: boolean;
}

export interface TimeAccountOvertimeData {
	overtimeSum?: number;
	overtimes?: TimeAccountOvertime[];
	overtimeSumDuration: number;
	overtimeSumDurationTotal: number;
	overtimeSumLastDurationTotal: number;
	allowancesOnly: boolean;
}

export interface TimeAccountTravellingTimeData {
	travellingTimeSum?: number;
	travellingTimes?: TimeAccountTravellingTime[];
	travellingTimeSumDuration: number;
	travellingTimeSumDurationTotal: number;
	travellingTimeSumLastDurationTotal: number;
}

export interface TimeAccountExitBookingBalanceData {
	workDurationCurrentTotalBalance: number;
	vacationTotalBalance: number;
	vacationUnit: VacationUnit;
	overtimeDurationCurrentTotalBalance: number;
	allowanceDurationCurrentTotalBalance: number;
	travellingTimesDurationCurrentTotalBalance: number;
	addOvertimes: boolean;
	addTravellingTimes: boolean;
}

export interface SaveTrialFeatureModulesResponse {
	reLoginRequired: boolean;
}

export interface Payment {
	id: number;
	paymentDate: string;
	amount: number;
	status: string;
	due: string;
	paid: boolean;
}

export interface Rebate {
	id: number;
	name: string;
	rebateType: string;
	discountPercentage: number;
	fixedPrice: number;
	visible: boolean;
	lifetime: boolean;
	trialOnly: boolean;
}

export interface RebateCode {
	id: number;
	rebate: Rebate;
	code: string;
}

export interface Address {
	id: number;
	address: string;
	city: string;
	state: string;
	zipCode: string;
	country: Country;
}

export interface Country {
	id: number;
	code: string;
	name: string;
}

export interface LocationRestrictionRadiusOption {
	value: string;
	label: string;
	id?: string;
}

export interface WorkingTimeRequestStepConfiguration {
	type: WorkingTimeRequestStepConfigurationType;
	userId?: number;
	approvalForCancellationRequired: boolean;
}

export interface UserSumsWorkingTime {
	durationDay: number;
	durationWeek: number;
	durationMonth: number;
}

export interface WorkDurations {
	workDurationPerDay: number;
	workDurationPerWeek: number;
	workDurationPerMonth: number;
}

export interface CombinedRecordingStatus {
	workingTimeStatus?: WorkingTimeStatus;
	projectTimeStatus?: ProjectTimeStatus;
}

export interface SaveWorkingTimeDateSpanResponse {
	durationDays: number;
	lastWorkingTime?: WorkingTime;
	nextAvailableDay: string;
}

export interface StatusElementEntries {
	recordingStatusEntries: RecordingStatusEntries;
	workingTimeRequestsToApprove?: number;
	currentUserValidationCount?: number;
	groupMemberValidationCount?: number;
	presenceBoardActiveUsersCount?: number;
}

export interface RecordingStatusEntries {
	runningWorkingTime?: RunningWorkingTime;
	runningProjectTime?: RunningProjectTime;
	runningDriveLog?: RunningDriveLog;
}

export interface ResetTrialDataResponse {
	hasWorkingTimeOrProjectTimes: boolean;
	hasWorkScheduleModels: boolean;
	hasBreakModels: boolean;
	hasTasks: boolean;
	hasDriveLogs: boolean;
	hasDriveLogCategories: boolean;
	hasCars: boolean;
	hasGroups: boolean;
}

export interface OnboardingDashboardAccountInfo {
	activeFeatureModules: FeatureModule[];
	activeBaseFeatureModules: FeatureModule[];
	activeAdditionalFeatureModules: FeatureModule[];
	workingTimeEnabled?: boolean;
	workingTimeRequestsEnabled?: boolean;
	projectTimeEnabled?: boolean;
	driveLogEnabled?: boolean;
}

/**
 * Response from `/pricing/getAvailableFeatureModules.json` — mirrors Java
 * {@code AvailableFeatureModulesResponse}. Unlike {@link PricingResponse},
 * this endpoint does not emit `activeFeatureModules`; callers supply that
 * from their own state when building a {@link FeatureModuleSelectionDialogConfig}.
 */
export interface AvailableFeatureModulesResponse {
	baseFeatureModules: FeatureModule[];
	extensionFeatureModules: FeatureModule[];
}

export interface FeatureModuleDiscountResult {
	moduleDiscountType: ModuleDiscountType;
	lowestPossiblePrice: number;
	totalDiscount?: number;
	percentageDiscount?: number;
	requiredModulesCount: number;
}

export interface CalculateFeatureModulePriceResult {
	featureModuleId: number;
	basePrice: number;
	pricePerUser: number;
	discountResult: FeatureModuleDiscountResult;
}

export interface FreeFeatureModuleResult {
	key: FeatureModuleKey;
	name: string;
	description: string;
	maxUsers: number;
	maxWorkingTimeTypes: number;
	maxActiveTasks: number;
	maxCars: number;
	maxDriveLogCategories: number;
}

export interface CalendarSubscriptionTokenResponse {
	calendarSubscriptionToken: string;
}

export interface IntegrationDetails {
	id: number;
	type: string;
}

/** Mirrors Java: com.troii.timr.web.model.FeatureModuleConfigurationResponse */
export interface FeatureModuleConfigurationResponse {
	licensedUsers: number;
	validTo: string;
	trial: boolean;
	activeFeatureModules: FeatureModule[];
	allFeatureModulesInConfiguration: FeatureModule[];
	free: boolean;
	trialAllowed: boolean;
	activeSubscription: boolean;
	activeSubscriptionVendor: string | null;
}

/** Mirrors Java: com.troii.timr.services.remote.HolidayRemoteService.HolidayCalculatorInfo */
export interface HolidayCalculatorInfo {
	includesChristmasEve: boolean;
	includesNewYearsEve: boolean;
}

/** Mirrors Java: com.troii.timr.dto.HolidayGenerationDiffDTO */
export interface HolidayGenerationDiffResponse {
	rangeStart: string;
	rangeEnd: string;
	toInsert: Holiday[];
	toUpdate: [Holiday, Holiday][];
	toDelete: Holiday[];
}

/** Mirrors Java: com.troii.timr.web.json.JSONExternalUserSyncResponse.User */
export interface ExternalSyncUser {
	id: string;
	firstName: string;
	lastName: string;
	email: string;
	userId: number | null;
	userLogin: string;
	userEmail: string;
	userFullName: string;
	linked: boolean;
	enabled: boolean;
	conflictedUserMessage?: string | null;
}

/** Mirrors Java: com.troii.timr.web.json.JSONExternalUserSyncResponse */
export interface ExternalUsersListResponse {
	conflictMessages?: string[];
	createUsers: ExternalSyncUser[];
	linkUsers: ExternalSyncUser[];
	updateUsers: ExternalSyncUser[];
}

/** Mirrors Java: com.troii.timr.dto.MobileAuthTokenDTO */
export interface MobileAuthTokenDTO {
	uuid: string;
}

/** Mirrors Java: com.troii.timr.dto.TerminalAuthTokenDTO */
export interface TerminalAuthTokenDTO {
	type: string;
	/** Instant serialized as ISO-8601 string */
	lastUsed: string;
}

/** Mirrors Java: com.troii.timr.services.integration.addison.AddisonOrganization */
export interface AddisonOrganization {
	id: string;
	name: string;
}

/** Mirrors Java: com.troii.timr.services.integration.addison.AddisonIntegrationHelperService.UsersWithoutEmployeeNumber */
export interface AddisonUsersWithoutEmployeeNumber {
	fullName: string;
}

/** Mirrors Java: com.troii.timr.services.pricing.FeatureModulePriceCalculationResult */
export interface FeatureModulePriceCalculationResult {
	monthlyPrice: number;
	yearlyPrice: number;
	pricePerUserMonthly: number;
	basePriceMonthly: number;
}

/** Mirrors Java: com.troii.timr.web.model.FeatureModulesLicensedUsersInfoResult */
export interface FeatureModulesLicensedUsersInfoResult {
	licensedUsers: number;
	activeUsers: number;
	validTo: string;
}

/** Mirrors Java: com.troii.timr.web.controller.timeaccounts.TimeAccountController.ValidateRecordingsResponse */
export interface ValidateRecordingsResponse {
	validationResults: ValidationResult[];
	totalCount: number;
}

/** Mirrors Java: com.troii.timr.web.model.WorkingTimeRequestCalculationResponse.TimeAccountData */
export interface WorkingTimeRequestCalculationTimeAccountData {
	lastWorkDurationTotalBalance: number;
	workDurationCurrentBalance: number;
	workDurationCurrentTotalBalance: number;
	unpaidTimeOffDurationPlanned: number;
	vacationYearFrom: string;
	vacationYearTo: string;
	vacationConsumed: number;
	vacationPlanned: number;
	vacationLeft: number;
	vacationUnit: VacationUnit;
}

/** Mirrors Java: com.troii.timr.web.model.WorkingTimeRequestCalculationResponse.VacationData */
export interface WorkingTimeRequestCalculationVacationData {
	vacationRequired: number;
	vacationUnit: VacationUnit;
	warningText: string;
}

/** Mirrors Java: com.troii.timr.web.model.WorkingTimeRequestCalculationResponse */
export interface WorkingTimeRequestCalculationResponse {
	timeAccountData: WorkingTimeRequestCalculationTimeAccountData;
	vacationData?: WorkingTimeRequestCalculationVacationData;
	workingDaysRequired?: number;
}

/** Mirrors Java: com.troii.timr.web.model.FilterUsersResponse */
export interface FilterUsersResponse {
	users: User[];
	success: boolean;
}

/** Mirrors Java: com.troii.timr.web.model.FilterUsersWithTaskLeaderResponse */
export interface FilterUsersWithTaskLeaderResponse {
	users: User[];
	success: boolean;
	isTaskLeader: boolean
}

/** Mirrors Java: com.troii.timr.web.model.SaveWorkingTimeResponse */
export interface SaveWorkingTimeResponse {
	workingTimes: WorkingTime[];
}

export interface IsSubstituteRequiredResponse {
	requiresSubstitute: boolean;
}

/** Mirrors Java: com.troii.timr.web.model.CountUnbalancedWorkingTimesResponse **/
export interface CountUnbalancedWorkingTimesResponse {
	foundWorkingTimes: number;
}

/** Mirrors Java: com.troii.timr.web.model.LoadOverTimeRulesResponse **/
export interface LoadOvertimeRulesResponse {
	overtimeRules: OvertimeRule[];
}

/** Mirrors Java: com.troii.timr.web.model.PaymentRedirectResponse **/
export interface PaymentRedirectResponse {
	redirectLocation: string;
}

/** Mirrors Java: com.troii.timr.web.model.ResignedUsersResponse **/
export interface ResignedUsersResponse {
	users: User[];
}

/** Mirrors Java: com.troii.timr.web.model.TimeAccountBalanceDatesResponse **/
export interface TimeAccountBalanceDatesResponse {
	balanceDates: string[];
}

/** Mirrors Java: com.troii.timr.web.model.TimeAccountPayoffMonthsResponse **/
export interface TimeAccountPayoffMonthsResponse {
	payoffMonths: string[];
}

/** Mirrors Java: com.troii.timr.web.model.CreateExportResponse **/
export interface CreateExportResponse {
	exportJobId: string;
}

export interface TableResponse<T> {
	draw: number;
	recordsTotal: number;
	recordsFiltered: number;
	data: T[];
}
